import React, {
  type Dispatch,
  type ReactElement,
  type SetStateAction,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonSelect,
  FooterActionBar,
  Icon,
  ToggleButton,
  ToggleButtonGroup,
} from '@saviynt/design-system';
import cx from 'classnames';

import { SodAdvancedFilters, SodHeader } from '../../../shared/components';
import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import { type RootState } from '../../../shared/types';
import { testIds } from '../../../tests';
import { allReports } from '../data-layer/violationsReportConstants';

import ReportTable from './ReportTable';

interface ReportPreviewProps {
  setShowReportModal: Dispatch<SetStateAction<boolean>>;
}

/**
 * ReportPreview
 */
function ReportPreview({
  setShowReportModal,
}: ReportPreviewProps): ReactElement {
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [selection, setSelection] = useState(1);
  const l = useInvertedLocalize();
  const [isSAP] = useState(true);

  const { dataTheme } = useTypedSelector(
    (state: RootState) => state.aag.global
  );

  const { selectedReport } = useTypedSelector(
    (state: RootState) => state.aag.report
  );

  const { filters, activeFilters } = useTypedSelector(
    (state) => state.aag.violationsTab.controls
  );

  const pageClasses = cx(
    'Sod-Report',
    isFilterExpanded && 'Sod-Report--isFilterExpanded'
  );

  const renderedHeaderControls = (
    <Box className='Sod-Report-headerControls'>
      <ToggleButtonGroup selection={selection} setSelection={setSelection}>
        <ToggleButton label={l('Summary')} />
        <ToggleButton label={l('Detail')} />
        {isSAP ? <ToggleButton label={l('Permission')} /> : null}
      </ToggleButtonGroup>
      <Box>
        <ButtonSelect
          label={l('Filter')}
          kind='rectangle'
          onClick={() => {
            setIsFilterExpanded(!isFilterExpanded);
          }}
          isOpen={isFilterExpanded}
          prefixIcon={<Icon kind='Filter' />}
        />
      </Box>

      <Button
        type='button'
        kind='outlined'
        size='medium'
        leftIcon={<Icon kind='Search' />}
      />
    </Box>
  );

  const renderedFilters = (
    <SodAdvancedFilters
      filters={filters}
      activeFilters={activeFilters}
      isClearBtn={false}
      searchDebounce={400}
      handleFilterChange={() => {}}
      handleFilterSearch={() => Promise.resolve()}
      handleOptionsClick={() => {}}
      handleClearBtn={() => {}}
    />
  );

  const renderHeader = () => (
    <SodHeader
      title={l(allReports[Number(selectedReport)])}
      controls={renderedHeaderControls}
      className='Sod-Report-header'
    />
  );

  const renderBody = () => (
    <article className='Sod-Report-content'>
      <Box className='Sod-Report-filtersBlock'>{renderedFilters}</Box>
      <ReportTable />
    </article>
  );

  const renderFooter = () => (
    <FooterActionBar className='Sod-Report-footerActionBar'>
      <Button
        type='button'
        kind='subtle'
        onClick={() => setShowReportModal(true)}>
        {l('View All Reports')}
      </Button>
      <Box className='Sod-Report-footerGroupRight'>
        <Button
          type='button'
          kind='filled'
          onClick={() => Logger.log('Hi Apply, How are you?')}>
          {l('Download')}
        </Button>
      </Box>
    </FooterActionBar>
  );

  return (
    <div
      data-theme={dataTheme}
      className={pageClasses}
      data-testid={testIds.SOD_REPORT_PAGE}>
      {renderHeader()}
      {renderBody()}
      {renderFooter()}
    </div>
  );
}

export default ReportPreview;
